// Notifications Page - View all notifications
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Bell, ArrowLeft, Calendar, Loader, MapPin, Users, Check, UserPlus, MessageSquare } from 'lucide-react';

dayjs.extend(relativeTime);

import { API_URL } from '../config/api';
import { socket } from '../socket';
const API = API_URL.replace(/\/api$/, '');

export default function Notifications({ token, onBack }: any) {
  const [eventsNotifs, setEventsNotifs] = useState<any[]>([]);
  const [bookingNotifs, setBookingNotifs] = useState<any[]>([]);
  const [verificationNotifs, setVerificationNotifs] = useState<any[]>([]);
  const [followers, setFollowers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'requests' | 'followers'>(() => {
    const v = (localStorage.getItem('auralink-notifs-filter') as any) || 'followers';
    const normalized = v === 'bookings' ? 'requests' : v; // migrate old value
    return (normalized === 'requests' || normalized === 'followers') ? (normalized as any) : 'followers';
  });
  const [bookingModal, setBookingModal] = useState<{ open: boolean; data?: any }>(() => ({ open: false }));
  const [tokenModal, setTokenModal] = useState<{ open: boolean; data?: any }>(() => ({ open: false }));
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [seenSet, setSeenSet] = useState<Set<string>>(new Set());

  // Build a unique seen key for follow notifications so re-follows reappear
  function followSeenKey(id: string, date?: string): string {
    const d = date ? String(date) : new Date().toISOString();
    return `follow:${String(id)}:${d}`;
  }

  function bumpUnread(countDelta: number = 1) {
    try {
      const raw = localStorage.getItem('auralink-unread-notifs') || '0';
      const n = parseInt(raw, 10) || 0;
      const next = Math.max(0, n + countDelta);
      localStorage.setItem('auralink-unread-notifs', String(next));
      window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count: next } }));
    } catch {}
  }

  function navigateView(newView: 'discover' | 'user-content') {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('view', newView);
      window.history.pushState({ view: newView }, '', url.toString());
      window.dispatchEvent(new PopStateEvent('popstate', { state: { view: newView } as any } as any));
      try { localStorage.setItem('auralink-current-view', newView); } catch {}
    } catch {}
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try { localStorage.setItem('auralink-notifs-filter', filter); } catch {}
  }, [filter]);

  // Load seen registry when user is available (merge legacy global key if present)
  useEffect(() => {
    try {
      const uid = String(currentUser?._id || currentUser?.id || '');
      if (!uid) return;
      const key = `auralink-notifs-seen:${uid}`;
      const list: string[] = JSON.parse(localStorage.getItem(key) || '[]');
      const legacy: string[] = JSON.parse(localStorage.getItem('auralink-notifs-seen') || '[]');
      const merged = Array.isArray(list) ? list.slice() : [];
      if (Array.isArray(legacy)) {
        for (const k of legacy) if (!merged.includes(k)) merged.push(k);
        try { localStorage.removeItem('auralink-notifs-seen'); } catch {}
      }
      // Migrate legacy follower seen keys (follow:<id>) to new policy — only click marks seen.
      // Drop keys without a timestamp to ensure followers persist until clicked.
      const cleaned = merged.filter((k) => !(k.startsWith('follow:') && k.split(':').length === 2));
      if (cleaned.length !== merged.length) {
        try { localStorage.setItem(key, JSON.stringify(cleaned)); } catch {}
      }
      setSeenSet(new Set(cleaned));
      localStorage.setItem(key, JSON.stringify(cleaned));
    } catch {}
  }, [currentUser]);

  // Realtime updates: listen to socket notifications and booking status updates
  useEffect(() => {
    function onNotification(payload: any) {
      try {
        if (!payload || !payload.kind) return;
        if (payload.kind === 'follow') {
          setFollowers((prev) => [
            {
              id: payload.user?._id || Math.random().toString(36),
              kind: 'follow',
              title: payload.title || `${payload.user?.username || 'Someone'} followed you`,
              message: payload.message || '',
              user: payload.user,
              date: new Date().toISOString(),
            },
            ...prev,
          ]);
        } else if (payload.kind === 'booking_received') {
          const item = {
            id: payload.bookingRequestId || Math.random().toString(36),
            kind: payload.kind,
            title: payload.requester?.username || 'New booking request',
            message: payload.message,
            date: payload.date || new Date().toISOString(),
            venue: payload.venue,
            requester: payload.requester,
            owner: payload.owner,
            status: payload.status,
            conversation: payload.conversation,
          } as any;
          setBookingNotifs((prev) => [item, ...prev]);
          bumpUnread(1);
        } else if (payload.kind === 'booking_token') {
          const requestId = String(payload.bookingRequestId || payload.requestId || payload.request?._id || '');
          const item = {
            id: requestId ? requestId + '-token' : (payload.token?._id || Math.random().toString(36)) + '-token',
            kind: 'booking_token',
            title: payload.owner?.username || 'Response',
            message: payload.message || `A response was issued for ${payload.venue?.name || 'your request'}`,
            date: payload.date || new Date().toISOString(),
            venue: payload.venue,
            requester: payload.requester,
            owner: payload.owner,
            status: 'token_generated',
            token: payload.token,
          } as any;
          setBookingNotifs((prev) => [item, ...prev]);
          bumpUnread(1);
        }
      } catch (e) {
        console.warn('Notification handler failed', e);
      }
    }

    function onBookingStatusUpdate({ bookingId, status, message }: any) {
      const item = {
        id: String(bookingId || Math.random().toString(36)),
        kind: 'booking_sent',
        title: 'Booking update',
        message: message || `Status: ${status}`,
        date: new Date().toISOString(),
        status,
      } as any;
      setBookingNotifs((prev) => [item, ...prev]);
    }

    try {
      socket.on('notification', onNotification);
      socket.on('booking_status_update', onBookingStatusUpdate);
    } catch {}
    // Also listen for latest follower preview updates from App (custom event)
    const onFollowerPreview = (e: any) => {
      try {
        const latest = e?.detail;
        if (!latest || !latest.id) return;
        const rawId = String(latest.id).replace(/^follow:/, '');
        const item = {
          id: rawId,
          kind: 'follow',
          title: `${latest.username || 'Someone'} followed you`,
          message: '',
          user: { _id: rawId, username: latest.username, avatar: latest.avatar },
          date: new Date(latest.at || Date.now()).toISOString(),
        } as any;
        setFollowers((prev) => {
          const exists = prev.some((f) => String(f.id) === String(item.id));
          if (exists) return prev;
          return [item, ...prev];
        });
        bumpUnread(1);
      } catch {}
    };
    window.addEventListener('auralink-last-follower.update', onFollowerPreview as any);
    return () => {
      try {
        socket.off('notification', onNotification);
        socket.off('booking_status_update', onBookingStatusUpdate);
      } catch {}
      window.removeEventListener('auralink-last-follower.update', onFollowerPreview as any);
    };
  }, []);

  async function loadAll() {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` } as any;
      const [eventsRes, receivedRes, sentRes, tokensReceivedRes, meRes, verifMineRes] = await Promise.all([
        axios.get(`${API}/api/events?status=published&limit=100`, { headers }),
        axios.get(`${API}/api/booking-requests/my/received`, { headers }),
        axios.get(`${API}/api/booking-requests/my/sent`, { headers }),
        axios.get(`${API}/api/tokens/my/received`, { headers }),
        axios.get(`${API}/api/users/me`, { headers }),
        axios.get(`${API}/api/verifications/requests/mine`, { headers }),
      ]);

      // Current user (needed for requester mapping and seeding selection)
      const me = meRes.data || {};

      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const eventsRaw = (eventsRes.data.events || []);
      // Only show upcoming events within 30 days
      const events = eventsRaw.filter((event: any) => {
        const d = new Date(event.startDate);
        return d >= now && d <= thirtyDaysFromNow;
      });
      // Helper: deterministic seeded RNG per day/user
      function xmur3(str: string) {
        let h = 1779033703 ^ str.length;
        for (let i = 0; i < str.length; i++) {
          h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
          h = (h << 13) | (h >>> 19);
        }
        return function () {
          h = Math.imul(h ^ (h >>> 16), 2246822507);
          h = Math.imul(h ^ (h >>> 13), 3266489909);
          h ^= h >>> 16;
          return h >>> 0;
        };
      }
      function mulberry32(a: number) {
        return function () {
          let t = (a += 0x6d2b79f5);
          t = Math.imul(t ^ (t >>> 15), t | 1);
          t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
          return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
      }
      function seededRandom(seed: string) {
        const seedFn = xmur3(seed);
        const a = seedFn();
        const rand = mulberry32(a);
        return rand();
      }

      // Score an event for relevance
      const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
      function scoreEvent(ev: any): number {
        const d = new Date(ev.startDate).getTime();
        const nowMs = now.getTime();
        const diff = Math.max(0, d - nowMs);
        const recency = 1 - Math.min(1, diff / WINDOW_MS);
        const participants = (ev.participants?.length || 0);
        const cap = (ev.capacity?.max || 0);
        const denom = cap > 0 ? cap : Math.max(1, participants);
        const popularity = Math.min(1, participants / denom);
        const dayKey = dayjs(ev.startDate).format('YYYY-MM-DD');
        const noise = seededRandom(String(me?._id || 'anon') + ':' + dayKey + ':' + String(ev._id || ev.id || 'x'));
        return recency * 0.6 + popularity * 0.3 + noise * 0.1;
      }

      // Only include events the current user has joined (registered).
      const myId = String(me?._id || '');
      const selectedEvents: any[] = events.filter((ev: any) => {
        const parts = Array.isArray(ev.participants) ? ev.participants : [];
        return parts.some((p: any) => String(p?._id || p) === myId);
      });

      const eventNotifications = selectedEvents.map((event: any) => ({
        id: event._id,
        kind: 'event',
        title: `Registered • Upcoming: ${event.title}`,
        message: `${dayjs(event.startDate).format('MMM D, YYYY')} • ${event.location?.city || event.location?.name || 'TBD'}`,
        time: dayjs(event.startDate).fromNow(),
        date: event.startDate,
        event,
      }));

      // Ensure events notifications are sorted newest-first
      const eventsSorted = [...eventNotifications].sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime(),
      );
      setEventsNotifs(eventsSorted);

      // Booking requests (received + sent)
      const received = (receivedRes.data?.requests || []).map((r: any) => ({
        id: r._id,
        kind: 'booking_received',
        title: r.requester?.username || 'New booking request',
        message: `From ${r.requester?.username || 'someone'}`,
        date: r.createdAt || new Date().toISOString(),
        venue: r.venue,
        requester: r.requester,
        owner: r.owner,
        status: r.status,
        conversation: r.conversation?._id || r.conversation,
      }));
      // Tokens received by requester: persist notifications so they appear even if realtime was missed
      const tokensReceived = (tokensReceivedRes.data?.tokens || []).map((t: any) => ({
        id: (t.request?._id || t.requestId || t.bookingRequest?._id || t.bookingRequestId || t._id || Math.random().toString(36)) + '-token',
        kind: 'booking_token',
        title: t.owner?.username || 'Response',
        message: `A response was issued for ${t.venue?.name || 'your request'}`,
        date: t.createdAt || t.expiresAt || new Date().toISOString(),
        venue: t.venue,
        requester: me, // current user
        owner: t.owner,
        status: 'token_generated',
        token: { code: t.code, expiresAt: t.expiresAt },
      }));

      // Requester side: include sent requests that have tokens generated as notifications fallback
      const sentTokenNotifs = (sentRes.data?.requests || [])
        .filter((r: any) => r.status === 'token_generated')
        .map((r: any) => ({
          id: (r._id || Math.random().toString(36)) + '-token',
          kind: 'booking_token',
          title: r.owner?.username || 'Response',
          message: `A response was issued for ${r.venue?.name || 'your request'}`,
          date: r.updatedAt || r.createdAt || new Date().toISOString(),
          venue: r.venue,
          requester: me,
          owner: r.owner,
          status: 'token_generated',
        }));

      // Combine, deduplicate by kind:id, then sort newest first
      const combinedBookings = Array.from(
        new Map(
          [...received, ...tokensReceived, ...sentTokenNotifs]
            .map((i: any) => [String(i.kind) + ':' + String(i.id), i])
        ).values()
      ).sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime(),
      );
      setBookingNotifs(combinedBookings);

      // Verification requests (professional / venue)
      const verifsRaw: any[] = (verifMineRes.data?.requests || []) as any[];
      const verifsMapped = verifsRaw.map((vr: any) => ({
        id: String(vr._id),
        kind: 'verification_request',
        title: vr.category === 'venue' ? 'Venue verification' : 'Professional verification',
        message: vr.desiredTier ? `Requested tier: ${vr.desiredTier}` : `Status: ${vr.status || 'pending'}`,
        date: vr.decidedAt || vr.createdAt || new Date().toISOString(),
        status: vr.status || 'pending',
        category: vr.category,
        venue: vr.venue,
        applicant: vr.applicant,
      }));
      const verifsSorted = [...verifsMapped].sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
      setVerificationNotifs(verifsSorted);

      // Followers: build list with a stable per-follower event date persisted in localStorage
      const uidStr = String(me?._id || me?.id || '');
      const followersList = (me.followers || []).map((u: any) => {
        const fid = String(u?._id || u);
        const storeKey = `auralink-follow-date:${uidStr}:${fid}`;
        let persistedDate = '';
        try { persistedDate = localStorage.getItem(storeKey) || ''; } catch {}
        // Prefer server-provided follow timestamp if available, else persisted, else now (and persist)
        const serverDate = (u.followedAt || u.createdAt || u.updatedAt); // optional fields
        let date = String(serverDate || persistedDate || new Date().toISOString());
        if (!persistedDate) {
          try { localStorage.setItem(storeKey, date); } catch {}
        }
        return {
          id: fid,
          kind: 'follow',
          title: `${u.username || 'Someone'} followed you`,
          message: '',
          user: u,
          date,
        } as any;
      });
      // Sort followers notifications newest-first
      let followersSorted = [...followersList].sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime(),
      );
      // Inject latest follower from localStorage (preview) if API hasn't reflected yet
      try {
        const rawLast = localStorage.getItem('auralink-last-follower') || '';
        const last = rawLast ? JSON.parse(rawLast) : null;
        if (last && last.id) {
          const lastDate = new Date(last.at || Date.now()).toISOString();
          const seenKey = followSeenKey(String(last.id), lastDate);
          const idx = followersSorted.findIndex((f) => String(f.id) === String(last.id));
          const isSeen = seenSet.has(seenKey);
          if (idx === -1 && !isSeen) {
            followersSorted = [
              {
                id: String(last.id),
                kind: 'follow',
                title: `${last.username || 'Someone'} followed you`,
                message: '',
                user: { _id: String(last.id), username: last.username, avatar: last.avatar },
                date: lastDate,
              },
              ...followersSorted,
            ];
          } else if (idx >= 0) {
            // Update existing follower item with latest event date so a re-follow can surface
            followersSorted[idx] = { ...followersSorted[idx], date: lastDate } as any;
          }
          // Persist the latest event date for this follower
          try { localStorage.setItem(`auralink-follow-date:${uidStr}:${String(last.id)}`, lastDate); } catch {}
        }
      } catch {}

      // Detect new followers since last session using a baseline list preserved across logout/login
      try {
        const uidForBase = String(me?._id || me?.id || '');
        const baseKey = `auralink-followers-baseline:${uidForBase}`;
        const currentIds: string[] = (me.followers || []).map((u: any) => String(u?._id || u));
        const prevBaseRaw = localStorage.getItem(baseKey) || '';
        const prevBase: string[] = prevBaseRaw ? JSON.parse(prevBaseRaw) : [];
        const newlyFollowed = Array.isArray(prevBase) && prevBase.length > 0
          ? currentIds.filter((id) => !prevBase.includes(id))
          : [];
        if (newlyFollowed.length > 0) {
          for (const fid of newlyFollowed) {
            const storeKey = `auralink-follow-date:${uidForBase}:${fid}`;
            const nowIso = new Date().toISOString();
            try { localStorage.setItem(storeKey, nowIso); } catch {}
            // Update followersSorted entry with latest date so it surfaces
            const idx = followersSorted.findIndex((f) => String(f.id) === String(fid));
            if (idx >= 0) followersSorted[idx] = { ...followersSorted[idx], date: nowIso } as any;
            else {
              followersSorted = [
                {
                  id: String(fid),
                  kind: 'follow',
                  title: `New follower`,
                  message: '',
                  user: { _id: String(fid) },
                  date: nowIso,
                },
                ...followersSorted,
              ];
            }
          }
        }
        // Update baseline to current set for next session comparison
        try { localStorage.setItem(baseKey, JSON.stringify(currentIds)); } catch {}
      } catch {}

      // Apply computed followers list after processing previews and newly followed detection
      setFollowers(followersSorted);

      // Initialize seen registry for user if missing
      try {
        const uid = String(me?._id || me?.id || '');
        const key = `auralink-notifs-seen:${uid}`;
        const list: string[] = JSON.parse(localStorage.getItem(key) || '[]');
        setSeenSet(new Set(Array.isArray(list) ? list : []));
      } catch {}

      setCurrentUser(me);
    } catch (err) {
      console.error('Load notifications error:', err);
    } finally {
      setLoading(false);
    }
  }

  // Reset unread count when visiting notifications page
  useEffect(() => {
    try {
      localStorage.setItem('auralink-unread-notifs', '0');
      localStorage.setItem('auralink-last-notifs-open', String(Date.now()));
      window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count: 0 } }));
    } catch {}
  }, []);

  const visibleEvents = useMemo(() => eventsNotifs.filter((n) => !seenSet.has(`${n.kind}:${n.id}`)), [eventsNotifs, seenSet]);
  const visibleBookings = useMemo(() => bookingNotifs.filter((n) => !seenSet.has(`${n.kind}:${n.id}`)), [bookingNotifs, seenSet]);
  const visibleVerifs = useMemo(() => verificationNotifs.filter((n) => !seenSet.has(`${n.kind}:${n.id}`)), [verificationNotifs, seenSet]);
  const visibleFollowers = useMemo(() => followers.filter((n) => {
    if (n.kind === 'follow') return !seenSet.has(followSeenKey(String(n.id), n.date));
    return !seenSet.has(`${n.kind}:${n.id}`);
  }), [followers, seenSet]);
  const currentList = useMemo(() => {
    if (filter === 'requests') return [...visibleVerifs, ...visibleBookings].sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
    if (filter === 'followers') return visibleFollowers;
    const all = [...visibleVerifs, ...visibleBookings, ...visibleFollowers];
    return all.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
  }, [filter, visibleVerifs, visibleBookings, visibleFollowers]);

  // Snapshot of all visible notifications (independent of current filter) for Sidebar to mirror
  const allVisibleForSnapshot = useMemo(() => {
    const all = [...visibleEvents, ...visibleVerifs, ...visibleBookings, ...visibleFollowers];
    return all.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
  }, [visibleEvents, visibleVerifs, visibleBookings, visibleFollowers]);

  function markSeen(notif: any) {
    try {
      const uid = String(currentUser?._id || currentUser?.id || '');
      if (!uid) return;
      const key = notif.kind === 'follow' ? followSeenKey(String(notif.id), notif.date) : `${notif.kind}:${notif.id}`;
      if (seenSet.has(key)) return;
      const next = new Set(seenSet);
      next.add(key);
      setSeenSet(next);
      const storeKey = `auralink-notifs-seen:${uid}`;
      const list = Array.from(next);
      localStorage.setItem(storeKey, JSON.stringify(list));
      // No-op: ack list replaced by baseline comparison
    } catch {}
  }

  async function openChatWith(userId: string) {
    try {
      const res = await axios.post(`${API}/api/conversations`, { partnerId: userId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.setItem('auralink-active-conversation', JSON.stringify(res.data));
      localStorage.setItem('auralink-in-dm', 'true');
      const url = new URL(window.location.href);
      url.searchParams.set('view', 'chat');
      window.location.href = url.toString();
    } catch (e) {
      console.error('Open chat failed', e);
      alert('Could not open chat. Please try again.');
    }
  }

  async function openConversationById(convId: string) {
    try {
      const res = await axios.get(`${API}/api/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      const conv = (res.data || []).find((c: any) => String(c._id) === String(convId));
      if (!conv) {
        alert('Conversation not found. Try starting a new chat.');
        return;
      }
      localStorage.setItem('auralink-active-conversation', JSON.stringify(conv));
      localStorage.setItem('auralink-in-dm', 'true');
      const url = new URL(window.location.href);
      url.searchParams.set('view', 'chat');
      window.location.href = url.toString();
    } catch (e) {
      console.error('Open conversation by id failed', e);
      alert('Could not open existing conversation. Please try again.');
    }
  }

  async function viewBookingRequest(brId: string) {
    try {
      const res = await axios.get(`${API}/api/booking-requests/${brId}`, { headers: { Authorization: `Bearer ${token}` } });
      setBookingModal({ open: true, data: res.data });
    } catch (e) {
      try {
        const status = (e as any)?.response?.status;
        if (status === 403) {
          alert('You are not authorized to view this booking request. Try replying in chat instead.');
          return;
        }
      } catch {}
      console.error('View booking request failed', e);
    }
  }

  // Publish snapshot to localStorage + event so Sidebar can mirror visibility
  useEffect(() => {
    try {
      const uid = String(currentUser?._id || currentUser?.id || '');
      if (!uid) return;
      const normalized = allVisibleForSnapshot.slice(0, 5).map((n: any) => ({
        id: String(n.id),
        kind: n.kind || 'event',
        title: n.title || '',
        message: n.message || (n.kind === 'follow' ? `${n.user?.username || 'Someone'} is now following you.` : ''),
        date: n.date || new Date().toISOString(),
        avatar: n.user?.avatar || n.avatar || '',
      }));
      const totalCount = allVisibleForSnapshot.length;
      const key = `auralink-notifs-visible-snapshot:${uid}`;
      localStorage.setItem(key, JSON.stringify(normalized));
      // Sync unread badge to visible count so sidebar mirrors the page
      try {
        localStorage.setItem('auralink-unread-notifs', String(totalCount));
        window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count: totalCount } }));
      } catch {}
      window.dispatchEvent(new CustomEvent('auralink-notifs.visible.update', { detail: { list: normalized, uid, count: totalCount } }));
    } catch {}
  }, [allVisibleForSnapshot, currentUser]);

  return (
    <>
      <div className="p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-heading">Notifications</h2>
              <p className="text-theme-secondary">All your important updates in one place</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Fancy Toggle (Requests ↔ Followers) */}
              <button
                onClick={() => setFilter(filter === 'requests' ? 'followers' : 'requests')}
                title={filter === 'requests' ? 'Showing Requests — click to show Followers' : 'Showing Followers — click to show Requests'}
                className="relative inline-flex items-center justify-between w-44 h-9 rounded-full border overflow-hidden transition-all"
                style={{ borderColor: 'var(--border)', background: 'linear-gradient(90deg, rgba(31,96,255,0.15) 0%, rgba(168,85,247,0.15) 100%)' }}
              >
                {/* Active pill indicator */}
                <span
                  className={`absolute inset-y-0 left-0 w-1/2 rounded-full transition-all duration-300 ${filter==='requests' ? 'translate-x-0 bg-green-500/70 dark:bg-green-500/60' : 'translate-x-full bg-blue-500/70 dark:bg-blue-500/60'}`}
                />
                {/* Labels */}
                <span className={`relative z-10 w-1/2 text-center text-xs font-semibold ${filter==='requests' ? 'text-white' : 'text-theme-secondary'}`}>Requests</span>
                <span className={`relative z-10 w-1/2 text-center text-xs font-semibold ${filter==='followers' ? 'text-white' : 'text-theme-secondary'}`}>Followers</span>
              </button>
              <span className="mx-3 h-6 w-px bg-[var(--border)]" />
              <button
                className="px-3 py-1 rounded-lg text-sm border"
                style={{ borderColor: 'var(--border)' }}
                onClick={() => {
                  try {
                    const uid = String(currentUser?._id || currentUser?.id || '');
                    if (!uid) return;
                    const allKeys: string[] = [
                      ...verificationNotifs.map((n) => `${n.kind}:${n.id}`),
                      ...bookingNotifs.map((n) => `${n.kind}:${n.id}`),
                      ...followers.map((n) => n.kind === 'follow' ? followSeenKey(String(n.id), n.date) : `${n.kind}:${n.id}`),
                    ];
                    const next = new Set([...Array.from(seenSet), ...allKeys]);
                    setSeenSet(next);
                    localStorage.setItem(`auralink-notifs-seen:${uid}`, JSON.stringify(Array.from(next)));
                    localStorage.setItem('auralink-unread-notifs', '0');
                    window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count: 0 } }));
                  } catch {}
                }}
                title="Mark all as seen"
              >Mark all seen</button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (currentList.length === 0 && visibleEvents.length === 0) ? (
            <div className="rounded-2xl p-12 text-center themed-card">
              <Bell className="w-16 h-16 text-theme-secondary mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-heading mb-2">No notifications</h3>
              <p className="text-theme-secondary">
                You're all caught up! Check back later for updates.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleEvents.length > 0 && (
                <div className="space-y-3">
                  {visibleEvents.map((notif: any) => (
                    <div
                      key={notif.id}
                      className={"rounded-lg p-3 hover:shadow-md transition-all duration-200 group themed-card cursor-pointer " + (!seenSet.has(`${notif.kind}:${notif.id}`) ? 'ring-1 ring-cyan-500/30 bg-cyan-500/5' : '')}
                      onClick={() => {
                        try {
                          markSeen(notif);
                          localStorage.setItem('auralink-highlight-event', notif.id);
                          localStorage.setItem('auralink-discover-category', 'sports');
                          navigateView('discover');
                        } catch {}
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <div className={'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ' + 'bg-purple-100 dark:bg-purple-900/30'}>
                          <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-heading mb-0.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {notif.title}
                          </h3>
                          <p className="text-theme-secondary mb-1 flex items-center gap-2 text-xs">
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {notif.event?.location?.city || notif.event?.location?.name || 'TBD'}</span>
                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {(notif.event?.participants?.length || 0)}/{(notif.event?.capacity?.max || 0)}</span>
                          </p>
                          <div className="flex items-center gap-2">
                            <span className={'px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'}>
                              Event
                            </span>
                            <span className="text-xs text-theme-secondary">{notif.time}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {currentList.map((notif: any) => (
                <div
                  key={notif.id}
                  className={"rounded-lg p-3 hover:shadow-md transition-all duration-200 group themed-card cursor-pointer " + (!seenSet.has(`${notif.kind}:${notif.id}`) ? 'ring-1 ring-cyan-500/30 bg-cyan-500/5' : '')}
                  onClick={() => {
                    try {
                      markSeen(notif);
                      if (notif.kind === 'event') {
                        localStorage.setItem('auralink-highlight-event', notif.id);
                        localStorage.setItem('auralink-discover-category', 'sports');
                        navigateView('discover');
                        return;
                      }
                      if (notif.kind === 'follow' && notif.user?._id) {
                        const uid = String(notif.user._id);
                        try { localStorage.setItem('auralink-open-profile', uid); } catch {}
                        try { window.dispatchEvent(new CustomEvent('auralink-open-profile', { detail: { id: uid } })); } catch {}
                        try { localStorage.removeItem('auralink-last-follower'); } catch {}
                        return;
                      }
                      if (notif.kind === 'booking_received' && notif.id) {
                        viewBookingRequest(String(notif.id));
                        return;
                      }
                      if (notif.kind === 'booking_token') {
                        setTokenModal({ open: true, data: notif.token || {} });
                        return;
                      }
                    } catch {}
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ' + (
                        notif.kind==='event' ? 'bg-purple-100 dark:bg-purple-900/30' :
                        notif.kind?.startsWith('booking') ? 'bg-green-100 dark:bg-green-900/30' :
                        'bg-blue-100 dark:bg-blue-900/30'
                      )}
                    >
                      {notif.kind==='event' && (<Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />)}
                      {notif.kind?.startsWith('booking') && (<Check className="w-4 h-4 text-green-600 dark:text-green-400" />)}
                      {notif.kind==='verification_request' && (<Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />)}
                      {notif.kind==='follow' && (<UserPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-heading mb-0.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {notif.title}
                      </h3>
                      {notif.kind==='event' && (
                        <>
                          <p className="text-theme-secondary mb-1 flex items-center gap-2 text-xs">
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {notif.event?.location?.city || notif.event?.location?.name || 'TBD'}</span>
                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {(notif.event?.participants?.length || 0)}/{(notif.event?.capacity?.max || 0)}</span>
                          </p>
                          <div className="flex items-center gap-2">
                            <span className={'px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'}>
                              Event
                            </span>
                            <span className="text-xs text-theme-secondary">{notif.time}</span>
                          </div>
                        </>
                      )}

                      {notif.kind==='booking_received' && (
                        <>
                          <p className="text-theme-secondary mb-1 text-xs">Venue: {notif.venue?.name || 'Venue'}</p>
                          <div className="flex items-center gap-2">
                            <span className={'px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}>
                              Booking Request
                            </span>
                            <span className="text-xs text-theme-secondary">{dayjs(notif.date).fromNow()}</span>
                          </div>
                        </>
                      )}

                      {notif.kind==='booking_token' && (
                        <>
                          <p className="text-theme-secondary mb-1 text-xs">Venue: {notif.venue?.name || 'Venue'}</p>
                          <div className="flex items-center gap-2">
                            <span className={'px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}>
                              Response Received
                            </span>
                            <span className="text-xs text-theme-secondary">{dayjs(notif.date).fromNow()}</span>
                          </div>
                        </>
                      )}

                      {notif.kind==='verification_request' && (
                        <>
                          <p className="text-theme-secondary mb-1 text-xs">Category: {notif.category === 'venue' ? 'Venue' : 'Professional'}</p>
                          <div className="flex items-center gap-2">
                            <span className={'px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'}>
                              Verification Request
                            </span>
                            <span className="text-xs text-theme-secondary">{(notif.status || 'pending').replace('_',' ')}</span>
                          </div>
                        </>
                      )}

                      {notif.kind==='follow' && (
                        <>
                          <p className="text-theme-secondary mb-1 text-xs">{notif.user?.username || 'Someone'} is now following you.</p>
                          <div className="flex items-center gap-2">
                            <span className={'px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}>
                              Follow
                            </span>
                          </div>
                        </>
                      )}

                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Booking Request Modal */}
      {bookingModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={()=>setBookingModal({ open: false })}>
          <div className="rounded-2xl p-6 themed-card w-[90%] max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">Booking Request</h3>
            <div className="text-sm text-theme-secondary mb-4">{bookingModal.data?.venue?.name || 'Venue'}</div>
            <div className="space-y-2 text-sm">
              <div><strong>Requester:</strong> {bookingModal.data?.requester?.username || 'User'}</div>
              <div><strong>Status:</strong> {bookingModal.data?.status}</div>
              {bookingModal.data?.notes ? (<div><strong>Notes:</strong> {bookingModal.data.notes}</div>) : null}
            </div>
            <div className="mt-4 flex gap-2">
              <button className="px-3 py-1 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} onClick={()=>{
                const convId = bookingModal.data?.conversation?._id || bookingModal.data?.conversation;
                if (convId) openConversationById(convId);
                else openChatWith(bookingModal.data?.requester?._id || bookingModal.data?.requester);
              }}>Open Chat</button>
              <button className="px-3 py-1 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} onClick={()=>setBookingModal({ open: false })}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Token Modal */}
      {tokenModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={()=>setTokenModal({ open: false })}>
          <div className="rounded-2xl p-6 themed-card w-[90%] max-w-md" onClick={(e)=>e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">Booking Token</h3>
            <div className="text-sm mb-2"><strong>Code:</strong> {tokenModal.data?.code || '—'}</div>
            <div className="text-sm mb-4"><strong>Expires:</strong> {tokenModal.data?.expiresAt ? dayjs(tokenModal.data.expiresAt).format('MMM D, YYYY h:mm A') : '—'}</div>
            <div className="flex gap-2">
              <button className="px-3 py-1 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} onClick={()=>setTokenModal({ open: false })}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
